/**
 * Utility functions for cache operations
 */

import { FILE_EXTENSION_TO_ASSET_TYPE } from "../config/constants";
import { AssetType, CacheEntry } from "../config/types";

/**
 * Determine the asset type from a URL or content type
 * @param url URL of the asset
 * @param contentType Optional content type
 * @returns Asset type
 */
export function determineAssetType(
  url: string,
  contentType?: string
): AssetType {
  // If content type is provided, use it to determine asset type
  if (contentType) {
    if (contentType.startsWith("image/")) {
      return AssetType.IMAGE;
    } else if (contentType.startsWith("video/")) {
      return AssetType.VIDEO;
    } else if (
      contentType.startsWith("font/") ||
      contentType === "application/font-woff" ||
      contentType === "application/font-woff2"
    ) {
      return AssetType.FONT;
    } else if (contentType.startsWith("audio/")) {
      return AssetType.AUDIO;
    }
  }

  // Otherwise, determine from URL extension
  const extension = url.split(".").pop()?.toLowerCase();
  if (!extension) {
    return AssetType.OTHER;
  }

  const mimeType = FILE_EXTENSION_TO_ASSET_TYPE[extension];
  if (!mimeType) {
    return AssetType.OTHER;
  }

  if (mimeType.startsWith("image/")) {
    return AssetType.IMAGE;
  } else if (mimeType.startsWith("video/")) {
    return AssetType.VIDEO;
  } else if (mimeType.startsWith("font/")) {
    return AssetType.FONT;
  } else if (mimeType.startsWith("audio/")) {
    return AssetType.AUDIO;
  }

  return AssetType.OTHER;
}

/**
 * Check if an asset should be cached based on its type and size
 * @param url URL of the asset
 * @param size Size of the asset in bytes
 * @param contentType Optional content type
 * @param maxSize Maximum size for cacheable assets
 * @returns Whether the asset should be cached
 */
export function shouldCacheAsset(
  url: string,
  size: number,
  contentType?: string,
  maxSize = Infinity
): boolean {
  // Don't cache assets that are too large
  if (size > maxSize) {
    return false;
  }

  // Don't cache assets from external domains unless explicitly allowed
  try {
    const urlObj = new URL(url, window.location.origin);
    if (urlObj.origin !== window.location.origin) {
      // Check if the domain is in the allowed list
      // This would be implemented based on your configuration
      // For now, we'll allow all domains
      // return isAllowedExternalDomain(urlObj.hostname);
    }
  } catch (error) {
    return false;
  }

  // Determine asset type
  const assetType = determineAssetType(url, contentType);

  // Cache based on asset type
  switch (assetType) {
    case AssetType.IMAGE:
    case AssetType.FONT:
      return true;
    case AssetType.VIDEO:
    case AssetType.AUDIO:
      // Only cache videos and audio if they're not too large
      return size < maxSize / 2;
    case AssetType.OTHER:
    default:
      // Don't cache other asset types by default
      return false;
  }
}

/**
 * Create a cache entry for an asset
 * @param data Asset data
 * @param url URL of the asset
 * @param contentType Optional content type
 * @param options Cache options
 * @returns Cache entry
 */
export function createCacheEntry<T>(
  data: T,
  url: string,
  contentType?: string,
  options?: {
    ttl?: number;
    tags?: string[];
    version?: string;
  }
): CacheEntry<T> {
  const now = Date.now();
  const assetType = determineAssetType(url, contentType);

  return {
    data,
    timestamp: now,
    expires: options?.ttl ? now + options.ttl * 1000 : undefined,
    type: assetType,
    tags: options?.tags,
    version: options?.version,
  };
}

/**
 * Check if a cache entry has expired
 * @param entry Cache entry
 * @returns Whether the entry has expired
 */
export function hasExpired(entry: CacheEntry<any>): boolean {
  return !!entry.expires && entry.expires < Date.now();
}

/**
 * Format a size in bytes to a human-readable string
 * @param bytes Size in bytes
 * @returns Human-readable size string
 */
export function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  } else if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  } else {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
}

/**
 * Log a debug message if debug mode is enabled
 * @param message Message to log
 * @param args Additional arguments
 */
export function debugLog(message: string, ...args: any[]): void {
  // Simple debug logging - can be controlled by the configuration
  // In development, you can enable it via localStorage for debugging
  if (
    typeof localStorage !== "undefined" &&
    localStorage.getItem("DEBUG_ASSET_CACHE") === "true"
  ) {
    console.log(`[AssetCache] ${message}`, ...args);
  }
}
